import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { auth, db } from '../../firebaseConfig';
import { collection, doc, setDoc, getDoc, getDocs } from 'firebase/firestore';
import Calendar from 'react-calendar';
import { Typography, Paper, Box, Button, MenuItem, Select, FormControl, InputLabel, Alert, Modal } from '@mui/material';
import './calendarPage.css';
import { fr } from 'date-fns/locale';
import { usePrompt, Prompt } from '../../components/usePrompt';
import ConfirmationDialogDataNotSaved from '../../components/confirmationDialogDataNoSaved';


const CalendarPage = () => {
  const [children, setChildren] = useState([]);
  const [selectedChild, setSelectedChild] = useState('');
  const [date, setDate] = useState(new Date());
  const [markedDates, setMarkedDates] = useState([]);
  const [availableDates, setAvailableDates] = useState([]);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [isEdited, setIsEdited] = useState(false);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newChild, setNewChild] = useState('');

  usePrompt("Vous avez des modifications non sauvegardées. Voulez-vous vraiment quitter cette page ?", isEdited);

  useEffect(() => {
    const fetchChildren = async () => {
      try {
        const user = auth.currentUser;
        if (user) {
          const childrenRef = collection(db, 'users', user.uid, 'children');
          const childrenSnap = await getDocs(childrenRef);
          const childrenList = childrenSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
          setChildren(childrenList);
          if (childrenList.length > 0) {
            setSelectedChild(childrenList[0].id);
          }
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des enfants :', err);
      }
    };

    fetchChildren();
  }, []);

  const fetchMarkedDates = async () => {
    if (selectedChild) {
      try {
        const childRef = doc(db, 'users', auth.currentUser.uid, 'children', selectedChild);
        const childSnap = await getDoc(childRef);
        if (childSnap.exists()) {
          const dates = childSnap.data().attendance || [];
          setMarkedDates(dates.map(dateStr => new Date(dateStr)));
        }
      } catch (err) {
        console.error('Erreur lors de la récupération des jours marqués :', err);
      }
    }
  };

  useEffect(() => {
    fetchMarkedDates();
  }, [selectedChild]);

  const fetchAvailableDates = async () => {
    try {
      const docRef = doc(db, 'settings', 'selectableDays');
      const docSnap = await getDoc(docRef);
      if (docSnap.exists()) {
        const days = docSnap.data().days || {};
        setAvailableDates(Object.keys(days).map(dateStr => new Date(dateStr)));
      }
    } catch (err) {
      console.error('Erreur lors de la récupération des jours disponibles :', err);
    }
  };

  useEffect(() => {
    fetchAvailableDates();
  }, []);

  const handleRefresh = async () => {
    setMarkedDates([]);
    setAvailableDates([]);
    setMessage('');
    setError('');
    setIsEdited(false);
    await fetchMarkedDates();
    await fetchAvailableDates();
  };

  const handleDateChange = (newDate) => {
    setDate(newDate);
  };

  const handleChildChange = (event) => {
    const newChildValue = event.target.value;
    if (isEdited) {
      setNewChild(newChildValue); // Stocker la nouvelle valeur pour utilisation ultérieure
      setDialogOpen(true); // Ouvrir la boîte de dialogue si des modifications ont été effectuées
    } else {
      setSelectedChild(newChildValue);
      setMessage('');
    }
  };

  const handleConfirmChange = () => {
    setDialogOpen(false);
    setSelectedChild(newChild); // Changer l'enfant sélectionné avec la nouvelle valeur
    setMessage('');
    setIsEdited(false);
  };
  const handleCancelChange = () => {
    setDialogOpen(false);
  };

  const toggleDateSelection = (value) => {
    const currentDate = new Date();
    const currentMonth = currentDate.getMonth();
    const selectedMonth = value.getMonth();
    const nextMonth = currentDate.getMonth() + 1;
    const currentDay = currentDate.getDate();
    const selectedYearCalendar = value.getFullYear();
    const isAugust = currentMonth === 7;
    const deadline = isAugust ? 26 : 20;
    const isCurrentMonthEditable = isAugust && currentDay <= deadline;

    if ((selectedMonth === nextMonth && currentDay > deadline) || (selectedMonth === currentMonth && !isCurrentMonthEditable) || (selectedYearCalendar < currentDate.getFullYear())) {
      alert(`La saisie des présences est fermée après le ${deadline} du mois en cours.`);
      return;
    }

    if (availableDates.some(availableDate => availableDate.toDateString() === value.toDateString())) {
      const formattedDate = value.toDateString();
      const newMarkedDates = markedDates.some(markedDate => markedDate.toDateString() === formattedDate)
        ? markedDates.filter(date => date.toDateString() !== formattedDate)
        : [...markedDates, value];

      setIsEdited(true);
      setMarkedDates(newMarkedDates);
    } else {
      alert('Ce jour n\'est pas disponible.');
    }
  };

  const handleSelectAllDates = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const isAugust = currentMonth === 7;
    const deadline = isAugust ? 26 : 20;
    const isAfterDeadline = today.getDate() > deadline;
    const isCurrentMonthEditable = isAugust && !isAfterDeadline;
    const currentMonthCalendar = date.getMonth();
    const currentYearCalendar = date.getFullYear();

    if (currentYearCalendar < today.getFullYear() || (currentMonth === currentMonthCalendar && currentYearCalendar == today.getFullYear() && !isCurrentMonthEditable) ) {
      alert(`La saisie des présences est fermée après le ${deadline} du mois en cours.`);
      return;
    }

    const datesToMark = availableDates.filter(availableDate => {
      const isPastDate = availableDate < today;
      const isNextMonth = isAfterDeadline && (availableDate.getMonth() === currentMonth || availableDate.getMonth() === currentMonth + 1);
      return !isPastDate && !isNextMonth && availableDate.getMonth() === currentMonthCalendar && availableDate.getFullYear() === currentYearCalendar;
    });

    let totalDate = [...markedDates, ...datesToMark];

    const uniqueDates = Array.from(new Set(totalDate.map(date => date.toDateString())))
      .map(dateString => new Date(dateString));

    setIsEdited(true);
    setMarkedDates(uniqueDates);
  };

  const handleSelectAllDatesOfYear = () => {
    const today = new Date();
    const currentMonth = today.getMonth();
    const isAugust = currentMonth === 7;
    const deadline = isAugust ? 26 : 20;
    const isAfterDeadline = today.getDate() > deadline;
    const isCurrentMonthEditable = isAugust && !isAfterDeadline;

    const filteredAvailableDates = availableDates.filter(date => {
      const isPastDate = date < today;
      const isSameMonth = currentMonth === date.getMonth() && !isCurrentMonthEditable;
      const isNextMonth = isAfterDeadline && (date.getMonth() === currentMonth || date.getMonth() === currentMonth + 1);
      return !isPastDate && !isNextMonth && !isSameMonth;
    });

    const totalDate = [...markedDates, ...filteredAvailableDates];

    const uniqueDates = Array.from(new Set(totalDate.map(date => date.toDateString())))
      .map(dateString => new Date(dateString));

    setMarkedDates(uniqueDates);
  };

  const handleSave = async () => {
    if (!selectedChild) {
      setError('Aucun enfant sélectionné pour enregistrer les jours.');
      return;
    }
    setError('');
    setMessage('');

    try {
      const user = auth.currentUser;
      if (user) {
        const childRef = doc(db, 'users', user.uid, 'children', selectedChild);
        await setDoc(childRef, { attendance: markedDates.map(date => date.toISOString()) }, { merge: true });
        setMessage('Les jours de présence ont été enregistrés avec succès. Pensez à inscrire tous vos enfants.');
        setIsEdited(false);
      }
    } catch (err) {
      setError('Erreur lors de l\'enregistrement des jours de présence.');
      console.error('Erreur lors de l\'enregistrement des jours de présence :', err);
    }
  };

  const handleMonthChange = (activeStartDate) => {
    const firstDayOfMonth = new Date(activeStartDate.getFullYear(), activeStartDate.getMonth(), 1);
    setDate(firstDayOfMonth);
    setMessage('');
    setError('');
  };

  return (
    <div>
      <Prompt when={isEdited} message='Vous avez des données non sauvegardées. En continuant, vous allez perdre ces données' />
      <Paper sx={{ padding: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', marginBottom: 2 }}>
          <Typography variant="h5" gutterBottom sx={{ marginRight: 2, fontWeight: 'bold' }}>
            Inscription à la cantine
          </Typography>
          <Button
            variant="contained"
            color="primary"
            sx={{ textTransform: 'none' }}
            component={Link}
            to="/profile"
          >
            Ajouter un enfant
          </Button>
        </Box>
        {children.length === 0 && (
          <Box sx={{ width: '100%' }}>
            <Alert severity="info">
              Aucun enfant ajouté. Veuillez ajouter au moins un enfant pour l'inscrire à la cantine.
            </Alert>
          </Box>
        )}
        {children.length > 0 && (
          <>
            <div style={{ marginRight: '16px', width: '100%' }}>  {/* Remplace FormControl */}
              <label htmlFor="select-child" style={{ display: 'block', marginBottom: '8px' }}>
                Enfant
              </label>
              <select
                id="select-child"
                value={selectedChild}
                onChange={handleChildChange}
                style={{ fontSize: '15px', width: '100%', padding: '4px', paddingBottom: '15px', paddingTop: '15px', borderRadius: '4px', border: '1px solid #ccc' }}
              >
                <option value="" disabled>
                  Sélectionnez un enfant
                </option>
                {children.map((child) => (
                  <option key={child.id} value={child.id}>
                    {child.name} {child.firstName}
                  </option>
                ))}
              </select>
            </div>

            <Box mt={2}>
              <Button
                sx={{ marginRight: 2, textTransform: 'none', marginTop: 1 }}
                variant="outlined"
                color="secondary"
                onClick={handleSelectAllDates}
              >
                Cocher tout le mois
              </Button>
              <Button
                sx={{ textTransform: 'none', marginTop: 1 }}
                variant="outlined"
                color="warning"
                onClick={handleSelectAllDatesOfYear}
              >
                Cocher toute l'année
              </Button>
            </Box>

            <Box mt={3}>
              <Calendar
                locale={fr}
                onChange={handleDateChange}
                value={date}
                onClickDay={toggleDateSelection}
                onActiveStartDateChange={({ activeStartDate }) => {
                  handleMonthChange(activeStartDate);
                }}
                tileDisabled={({ date }) => !availableDates.some(availableDate => availableDate.toDateString() === date.toDateString())}
                tileClassName={({ date, view }) => {
                  if (markedDates.some(markedDate => markedDate.toDateString() === date.toDateString())) {
                    return 'marked-date';
                  }
                  return '';
                }}
              />
            </Box>

            <Box mt={2}>
              <Button
                sx={{ marginRight: 2 }}
                variant="outlined"
                color="error"
                onClick={handleRefresh}
                disabled={!isEdited}
              >
                Annuler
              </Button>

              <Button
                variant="contained"
                color="success"
                onClick={handleSave}
              >
                Sauvegarder
              </Button>
            </Box>

            {message && <Alert severity="success">{message}</Alert>}
            {error && <Alert severity="error">{error}</Alert>}
          </>
        )}
      </Paper>
      <ConfirmationDialogDataNotSaved
        open={dialogOpen}
        onClose={handleCancelChange}
        onConfirm={handleConfirmChange}
      />
    </div>
  );
};

export default CalendarPage;
